import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { CostLineItem, CostCategory, COST_CATEGORY_LABELS, COST_CATEGORY_UNITS, DEFAULT_TAX_RATE } from '../../types/billing';
import { Button } from '../ui/Button';
import { CostLineItemRow } from './CostLineItemRow';
import { CostSummaryCard } from './CostSummaryCard';
import { TaxCalculator } from './TaxCalculator';

interface CostEntryFormProps {
  workOrderId: string;
  complaintId: string;
  vendorId: string;
  initialCost?: {
    materials: CostLineItem[];
    labor: CostLineItem[];
    service: CostLineItem[];
    other: CostLineItem[];
    tax_rate: number;
  };
  onSubmit: (materials: CostLineItem[], labor: CostLineItem[], service: CostLineItem[], other: CostLineItem[], taxRate: number) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

type CostTab = 'material' | 'labor' | 'service' | 'other';

export const CostEntryForm: React.FC<CostEntryFormProps> = ({
  workOrderId,
  complaintId,
  vendorId,
  initialCost,
  onSubmit,
  onCancel,
  isSaving = false,
}) => {
  const [activeTab, setActiveTab] = useState<CostTab>('material');
  const [materials, setMaterials] = useState<CostLineItem[]>(initialCost?.materials || []);
  const [labor, setLabor] = useState<CostLineItem[]>(initialCost?.labor || []);
  const [service, setService] = useState<CostLineItem[]>(initialCost?.service || []);
  const [other, setOther] = useState<CostLineItem[]>(initialCost?.other || []);
  const [taxRate, setTaxRate] = useState(initialCost?.tax_rate || DEFAULT_TAX_RATE);

  const [newDesc, setNewDesc] = useState('');
  const [newQty, setNewQty] = useState(1);
  const [newUnit, setNewUnit] = useState('pcs');
  const [newRate, setNewRate] = useState(0);

  const getItems = (tab: CostTab): CostLineItem[] => {
    switch (tab) {
      case 'material': return materials;
      case 'labor': return labor;
      case 'service': return service;
      case 'other': return other;
    }
  };

  const setItems = (tab: CostTab, items: CostLineItem[]) => {
    switch (tab) {
      case 'material': setMaterials(items); break;
      case 'labor': setLabor(items); break;
      case 'service': setService(items); break;
      case 'other': setOther(items); break;
    }
  };

  const handleAddItem = () => {
    if (!newDesc.trim() || newRate <= 0) return;
    const newItem: CostLineItem = {
      item_id: `CLI-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      description: newDesc.trim(),
      quantity: newQty,
      unit: newUnit,
      unit_price: newRate,
      amount: newQty * newRate,
      category: activeTab,
    };
    setItems(activeTab, [...getItems(activeTab), newItem]);
    setNewDesc('');
    setNewQty(1);
    setNewRate(0);
  };

  const handleRemoveItem = (itemId: string) => {
    setItems(activeTab, getItems(activeTab).filter(i => i.item_id !== itemId));
  };

  const handleSubmit = () => {
    onSubmit(materials, labor, service, other, taxRate);
  };

  const currentItems = getItems(activeTab);
  const allItems = [...materials, ...labor, ...service, ...other];

  return (
    <div className="space-y-4">
      <div className="flex border-b border-slate-800 gap-1">
        {COST_CATEGORY_UNITS.map((cat) => {
          const count = getItems(cat).length;
          return (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg transition-colors ${
                activeTab === cat
                  ? 'bg-slate-800 text-sky-400 border-b-2 border-sky-400'
                  : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              {COST_CATEGORY_LABELS[cat]} ({count})
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {currentItems.map((item) => (
          <CostLineItemRow key={item.item_id} item={item} onRemove={handleRemoveItem} />
        ))}
        {currentItems.length === 0 && (
          <p className="text-xs text-slate-500 text-center py-4">No items in this category. Add one below.</p>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2 p-3 bg-slate-950 rounded-lg border border-slate-800">
        <div className="flex-1 min-w-[140px]">
          <label className="text-[10px] text-slate-500 mb-1 block">Description</label>
          <input
            type="text"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Item description"
            className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
          />
        </div>
        <div className="w-16">
          <label className="text-[10px] text-slate-500 mb-1 block">Qty</label>
          <input
            type="number"
            min={1}
            value={newQty}
            onChange={(e) => setNewQty(parseInt(e.target.value) || 1)}
            className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
          />
        </div>
        <div className="w-16">
          <label className="text-[10px] text-slate-500 mb-1 block">Unit</label>
          <select
            value={newUnit}
            onChange={(e) => setNewUnit(e.target.value)}
            className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
          >
            <option value="pcs">pcs</option>
            <option value="hrs">hrs</option>
            <option value="kg">kg</option>
            <option value="mtr">mtr</option>
            <option value="lot">lot</option>
            <option value="nos">nos</option>
          </select>
        </div>
        <div className="w-24">
          <label className="text-[10px] text-slate-500 mb-1 block">Rate (Rs)</label>
          <input
            type="number"
            min={0}
            value={newRate || ''}
            onChange={(e) => setNewRate(parseFloat(e.target.value) || 0)}
            className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
          />
        </div>
        <Button variant="secondary" size="sm" onClick={handleAddItem} icon={<Plus className="w-3.5 h-3.5" />} disabled={!newDesc.trim() || newRate <= 0}>
          Add
        </Button>
      </div>

      <CostSummaryCard materials={materials} labor={labor} service={service} other={other} taxRate={taxRate} />
      <TaxCalculator taxRate={taxRate} onTaxRateChange={setTaxRate} subtotal={allItems.reduce((s, i) => s + i.amount, 0)} />

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} isLoading={isSaving} disabled={allItems.length === 0}>
          Save Cost Entry
        </Button>
      </div>
    </div>
  );
};
